import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createFeatureRequest,
  getFeatures,
  removeFeature,
  updateFeatureRequest,
} from "../../../services/api/plansFeatures/featuresApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const FEATURE_CREATE_ERROR_FALLBACK = {
  en: "Failed to create feature.",
  ar: "فشل في إنشاء الميزة.",
};

const FEATURE_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update feature.",
  ar: "فشل في تحديث الميزة.",
};

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

export const fetchFeaturesData = createAsyncThunk(
  "features/fetchFeaturesData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getFeatures();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch features data",
      );
    }
  },
);

export const createFeature = createAsyncThunk(
  "features/createFeature",
  async (featureData, { rejectWithValue }) => {
    try {
      const { name_en, name_ar, status } = featureData;
      const response = await createFeatureRequest({ name_en, name_ar, status });

      const apiError = rejectApiResponse(
        response.data,
        FEATURE_CREATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        feature: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, FEATURE_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const editFeature = createAsyncThunk(
  "features/editFeature",
  async ({ id, featureData }, { rejectWithValue }) => {
    try {
      const { name_en, name_ar, status } = featureData;
      const response = await updateFeatureRequest(id, { name_en, name_ar, status });

      const apiError = rejectApiResponse(
        response.data,
        FEATURE_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        feature: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, FEATURE_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const deleteFeature = createAsyncThunk(
  "features/deleteFeature",
  async (id, { rejectWithValue }) => {
    try {
      await removeFeature(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete feature",
      );
    }
  },
);

const featuresSlice = createSlice({
  name: "features",
  initialState: {
    featuresData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeaturesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeaturesData.fulfilled, (state, action) => {
        state.loading = false;
        state.featuresData = action.payload;
      })
      .addCase(fetchFeaturesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFeature.fulfilled, (state, action) => {
        if (action.payload?.feature) {
          state.featuresData.push(action.payload.feature);
        }
      })
      .addCase(createFeature.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editFeature.fulfilled, (state, action) => {
        const feature = action.payload?.feature;
        if (!feature) return;

        const index = state.featuresData.findIndex(
          (item) => item.id === feature.id,
        );
        if (index !== -1) {
          state.featuresData[index] = feature;
        }
      })
      .addCase(editFeature.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteFeature.fulfilled, (state, action) => {
        state.featuresData = state.featuresData.filter(
          (feature) => feature.id !== action.payload,
        );
        const toastMessages = {
          en: "Feature deleted successfully!",
          ar: "تم حذف الميزة بنجاح!",
        };
        toast.success(toastMessages[getLang()]);
      })
      .addCase(deleteFeature.rejected, (state, action) => {
        state.error = action.payload;
        const toastMessages = {
          en: "Failed to delete feature.",
          ar: "فشل في حذف الميزة.",
        };
        toast.error(toastMessages[getLang()]);
      });
  },
});

export default featuresSlice.reducer;
